<template>
  <div>
    <main-navigation></main-navigation>

    <div class="statement-page">
      <!-- HEADER -->
      <div class="statement-header">
        <router-link :to="`/tenant/${tenantId}`" class="back-link">
          <i class="bi bi-arrow-left"></i>
          <span>Back to Tenant Profile</span>
        </router-link>
        <h1>Tenant Statement</h1>
        <button class="btn-primary" @click="printStatement">
          <i class="bi bi-printer"></i> Print
        </button>
      </div>

      <div v-if="loading" class="loading-tile">
        <i class="bi bi-arrow-repeat spin"></i>
        <p>Loading statement...</p>
      </div>

      <div v-else id="printable-statement">
        <!-- TENANT DETAILS CARD -->
        <div class="details-card">
          <div class="details-avatar">
            {{ getInitials(tenant.tenant_name) }}
          </div>
          <div class="details-info">
            <h2>{{ tenant.tenant_name || "—" }}</h2>
            <div class="details-grid">
              <div class="detail-item">
                <span class="detail-label"><i class="bi bi-card-text"></i> ID Number</span>
                <span class="detail-value">{{ tenant.tenant_idnumber || "—" }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label"><i class="bi bi-telephone"></i> Phone</span>
                <span class="detail-value">{{ tenant.tenant_phone || "—" }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label"><i class="bi bi-door-open"></i> Unit</span>
                <span class="detail-value">{{ tenant.unit_number || "—" }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label"><i class="bi bi-building"></i> House</span>
                <span class="detail-value">{{ tenant.house_name || "—" }}</span>
              </div>
            </div>
          </div>
          <div class="details-balance">
            <span class="balance-label">Closing Balance</span>
            <span
              class="balance-value"
              :class="closingBalance > 0 ? 'negative' : 'positive'"
            >
              {{ formatCurrency(Math.abs(closingBalance)) }}
            </span>
            <span class="balance-caption">
              {{ closingBalance > 0 ? "Owed by tenant" : "Credit / wallet" }}
            </span>
          </div>
        </div>

        <!-- STATEMENT TABLE -->
        <div class="statement-table-wrapper">
          <table class="statement-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th class="align-right">Amount</th>
                <th class="align-center">Debit/Credit</th>
                <th class="align-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!transactions.length">
                <td colspan="5" class="empty-row">
                  No transactions found for this tenant yet.
                </td>
              </tr>
              <tr v-for="(txn, index) in transactions" :key="index">
                <td>{{ formatDate(txn.date) }}</td>
                <td>
                  <span class="txn-description">{{ txn.description }}</span>
                  <span v-if="txn.detail" class="txn-detail">{{ txn.detail }}</span>
                </td>
                <td class="align-right amount-cell">
                  {{ formatCurrency(txn.debit > 0 ? txn.debit : txn.credit) }}
                </td>
                <td class="align-center">
                  <span class="dc-badge" :class="txn.debit > 0 ? 'debit-cell' : 'credit-cell'">
                    {{ txn.debit > 0 ? "DR" : "CR" }}
                  </span>
                </td>
                <td class="align-right balance-cell" :class="txn.balance > 0 ? 'negative' : 'positive'">
                  {{ formatCurrency(Math.abs(txn.balance)) }}
                  <span class="balance-tag">{{ txn.balance > 0 ? "DR" : "CR" }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from "@/services/api.js";

export default {
  data() {
    return {
      tenantId: null,
      tenant: {},
      transactions: [],
      closingBalance: 0,
      loading: true,
    };
  },

  async mounted() {
    this.tenantId = this.$route.params.tenant_id;
    await this.getStatement();
  },

  methods: {
    async getStatement() {
      this.loading = true;
      try {
        const response = await apiFetch(`/tenants/${this.tenantId}/statement`);
        console.log("Statement response:", response); // TEMP — see what actually comes back

        if (!response.success) {
          console.error("Statement fetch failed:", response.error);
          return;
        }

        const data = response.data || {};
        this.tenant = data.tenant || {};
        this.transactions = data.transactions || [];
        this.closingBalance = data.closing_balance || 0;
      } catch (error) {
        console.error("Statement fetch error", error);
      } finally {
        this.loading = false;
      }
    },

    getInitials(name) {
      if (!name) return "T";
      return name
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },

    formatCurrency(value) {
      if (!value && value !== 0) return "KES 0";
      return new Intl.NumberFormat("en-KE", {
        style: "currency",
        currency: "KES",
        minimumFractionDigits: 0,
      }).format(value);
    },

    formatDate(date) {
      if (!date) return "—";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    printStatement() {
      window.print();
    },
  },
};
</script>

<style scoped>
.statement-page {
  padding: 24px 32px;
  background: var(--dash-bg);
  min-height: calc(100vh - 80px);
  font-family: var(--dash-font);
}

.statement-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  background: white;
  padding: 16px 24px;
  border-radius: var(--dash-radius-lg);
  box-shadow: 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.back-link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--dash-muted);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: var(--dash-pill);
  transition: all 0.2s;
}
.amount-tag {
  font-size: 10px;
  font-weight: 600;
  margin-left: 4px;
  opacity: 0.75;
}

.back-link:hover {
  background: var(--dash-bg-alt);
  color: var(--dash-text);
}

.statement-header h1 {
  font-size: 20px;
  font-weight: 600;
  color: var(--dash-ink);
  margin: 0;
}
.align-center {
  text-align: center;
}

.dc-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: var(--dash-pill);
  font-size: 11px;
  font-weight: 700;
}

.dc-badge.debit-cell {
  background: var(--dash-danger-soft);
  color: var(--dash-danger-ink);
}

.dc-badge.credit-cell {
  background: var(--dash-success-soft);
  color: var(--dash-success-ink);
}

.btn-primary {
  background: var(--dash-primary);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: var(--dash-pill);
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: var(--dash-shadow);
}

.btn-primary:hover {
  background: var(--dash-primary-hover);
  transform: translateY(-2px);
}

.loading-tile {
  text-align: center;
  padding: 60px 20px;
  color: var(--dash-muted);
}

.spin {
  animation: spin 1s linear infinite;
  font-size: 1.5rem;
  display: inline-block;
  margin-bottom: 8px;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Details Card */
.details-card {
  background: white;
  border-radius: var(--dash-radius-xl);
  padding: 24px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 24px;
  box-shadow: 0 8px 20px -4px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.02);
}

.details-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 600;
  color: white;
  background: linear-gradient(145deg, #8b5cf6, #7c3aed);
  flex-shrink: 0;
}

.details-info {
  flex: 2;
}

.details-info h2 {
  font-size: 20px;
  font-weight: 700;
  color: var(--dash-ink);
  margin: 0 0 12px 0;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  font-size: 11px;
  color: var(--dash-muted);
  display: flex;
  align-items: center;
  gap: 6px;
}

.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--dash-text);
}

.details-balance {
  text-align: center;
  background: var(--dash-surface-2);
  padding: 16px 24px;
  border-radius: var(--dash-radius-lg);
  flex-shrink: 0;
  min-width: 160px;
}

.balance-label {
  display: block;
  font-size: 12px;
  color: var(--dash-muted);
  margin-bottom: 4px;
}

.balance-value {
  display: block;
  font-size: 22px;
  font-weight: 700;
}

.balance-value.negative {
  color: var(--dash-danger);
}

.balance-value.positive {
  color: var(--dash-success);
}

.balance-caption {
  display: block;
  font-size: 11px;
  color: var(--dash-muted);
  margin-top: 2px;
}

/* Statement Table */
.statement-table-wrapper {
  background: white;
  border-radius: var(--dash-radius-xl);
  padding: 8px;
  box-shadow: 0 8px 20px -4px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.02);
  overflow-x: auto;
}

.statement-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.statement-table thead th {
  text-align: left;
  padding: 14px 16px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--dash-muted);
  border-bottom: 2px solid var(--dash-bg-alt);
}

.statement-table thead th.align-right {
  text-align: right;
}

.statement-table thead th.align-center {
  text-align: center;
}

.statement-table tbody td {
  padding: 14px 16px;
  border-bottom: 1px solid var(--dash-line);
  vertical-align: top;
}

.statement-table tbody tr:last-child td {
  border-bottom: none;
}

.statement-table tbody tr:hover {
  background: var(--dash-surface-2);
}

.align-right {
  text-align: right;
}

.txn-description {
  display: block;
  font-weight: 600;
  color: var(--dash-ink);
}

.txn-detail {
  display: block;
  font-size: 12px;
  color: var(--dash-muted);
  margin-top: 2px;
}

.debit-cell {
  color: var(--dash-danger);
  font-weight: 600;
}

.credit-cell {
  color: var(--dash-success);
  font-weight: 600;
}

.balance-cell {
  font-weight: 700;
}

.balance-cell.negative {
  color: var(--dash-danger);
}

.balance-cell.positive {
  color: var(--dash-success);
}

.balance-tag {
  font-size: 10px;
  font-weight: 600;
  color: var(--dash-muted);
  margin-left: 4px;
}

.empty-row {
  text-align: center;
  padding: 40px 20px;
  color: var(--dash-muted);
}

/* Print styles */
@media print {
  .statement-header,
  main-navigation {
    display: none !important;
  }

  .statement-page {
    padding: 0;
    background: white;
  }

  .details-card,
  .statement-table-wrapper {
    box-shadow: none;
    border: 1px solid #ddd;
  }
}

/* Responsive */
@media (max-width: 900px) {
  .details-card {
    flex-direction: column;
    text-align: center;
  }

  .details-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .statement-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }
}
</style>
